import { useEffect } from 'react';
import { useSettings } from '@/state/settings';
import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useMarkets } from '@/state/markets';
import { useMeta } from '@/state/meta';
import { ACHIEVEMENTS } from '@/engine/achievements';
import { TRIBE_LABEL, TRIBE_COLOR, type TribeId } from '@/content/xcloneHandles';
import { money } from '@/lib/format';
import { cn } from '@/lib/cn';

export function StatsDashboard() {
  const open = useSettings((s) => s.showLifeDashboard);
  const close = useSettings((s) => s.setShowLifeDashboard);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  if (!open) return null;

  return (
    <>
      <button
        onClick={() => close(false)}
        aria-label="Close stats"
        className="fixed inset-0 z-40 bg-black/40"
        tabIndex={-1}
      />
      <aside className="fixed left-1/2 top-1/2 z-50 max-h-[80vh] w-[min(92vw,560px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded border border-[var(--border-strong)] bg-[var(--bg-deep)] p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-[var(--border-subtle)] pb-2">
          <h2 className="text-mono-md font-bold text-[var(--text-primary)]">Stats Dashboard</h2>
          <button
            onClick={() => close(false)}
            className="text-mono-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            close [esc]
          </button>
        </div>
        <MarketsBlock />
        <PowerBlock />
        <LifeBlock />
        <AchievementsBlock />
        <p className="mt-4 text-[10px] text-[var(--text-muted)] italic">
          stats hide by default. you opted in. don't stare too long.
        </p>
      </aside>
    </>
  );
}

function MarketsBlock() {
  const cash = useMarkets((s) => s.cash);
  const realized = useMarkets((s) => s.realizedPnl);
  const positions = useMarkets((s) => s.positions);
  const equityATH = useMarkets((s) => s.equityATH);
  const fills = useMarkets((s) => s.recentFills);

  // Derive trade stats from the recent-fills buffer (last 50). Pair sells with the
  // earlier buy on the same ticker to estimate per-trade P&L. Best-effort — exact
  // P&L tracking would require persisting every closed lot.
  let trades = 0, wins = 0, losses = 0, totalWin = 0, totalLoss = 0, biggestWin = 0, biggestLoss = 0;
  // Naive lot-matching: walk newest→oldest, when we see a sell, find the most recent older buy on same ticker
  const seenSell = new Set<number>();
  for (let i = 0; i < fills.length; i++) {
    const sell = fills[i]!;
    if (sell.side !== 'sell' || seenSell.has(i)) continue;
    for (let j = i + 1; j < fills.length; j++) {
      const buy = fills[j]!;
      if (buy.ticker !== sell.ticker || buy.side !== 'buy') continue;
      const pnlPerShare = sell.price - buy.price;
      const pnl = pnlPerShare * Math.min(sell.qty, buy.qty);
      trades++;
      if (pnl > 0) { wins++; totalWin += pnl; if (pnl > biggestWin) biggestWin = pnl; }
      else if (pnl < 0) { losses++; totalLoss += Math.abs(pnl); if (Math.abs(pnl) > biggestLoss) biggestLoss = Math.abs(pnl); }
      seenSell.add(i);
      break;
    }
  }
  const winRate = trades > 0 ? wins / trades : 0;
  const avgWin = wins > 0 ? totalWin / wins : 0;
  const avgLoss = losses > 0 ? totalLoss / losses : 0;

  return (
    <Section title="Markets">
      <Row label="Cash" value={money(cash)} />
      <Row label="Realized P&L" value={money(realized)} positive={realized >= 0} />
      <Row label="Open Positions" value={positions.length.toString()} />
      {equityATH > 0 && <Row label="Equity ATH" value={money(equityATH)} />}
      {trades > 0 && (
        <>
          <Row label={`Win rate (last ${trades})`} value={`${(winRate * 100).toFixed(0)}% (${wins}W/${losses}L)`} positive={winRate >= 0.5} />
          {wins > 0 && <Row label="Avg win" value={money(avgWin)} positive />}
          {losses > 0 && <Row label="Avg loss" value={money(-avgLoss)} positive={false} />}
          {biggestWin > 0 && <Row label="Biggest win" value={money(biggestWin)} positive />}
          {biggestLoss > 0 && <Row label="Biggest loss" value={money(-biggestLoss)} positive={false} />}
        </>
      )}
    </Section>
  );
}

function AchievementsBlock() {
  const unlocked = useMeta((s) => s.unlockedAchievements);
  const unlockedSet = new Set(unlocked);
  const total = ACHIEVEMENTS.length;
  const got = unlocked.length;
  return (
    <Section title={`Achievements (${got}/${total})`}>
      <div className="grid grid-cols-1 gap-1">
        {ACHIEVEMENTS.map((a) => {
          const got = unlockedSet.has(a.id);
          return (
            <div
              key={a.id}
              className={cn(
                'flex items-baseline justify-between gap-2 rounded border px-2 py-1 text-mono-xs transition-opacity',
                got
                  ? 'border-[var(--positive)]/40 bg-[var(--positive)]/5'
                  : 'border-[var(--border-subtle)] opacity-50',
              )}
              title={a.description}
            >
              <span className={cn('font-bold', got ? 'text-[var(--positive)]' : 'text-[var(--text-muted)]')}>
                {got ? '✓' : '·'} {a.label}
              </span>
              <span className="truncate text-right text-[10px] text-[var(--text-secondary)]">{a.description}</span>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function PowerBlock() {
  const tribeRep = usePower((s) => s.tribeRep);
  const notoriety = usePower((s) => s.notoriety);
  const influence = usePower((s) => s.influence);

  return (
    <Section title="Power">
      <Row label="Notoriety" value={`${notoriety}/100`} />
      <Row label="Influence" value={`${influence}/100`} />
      <div className="mt-2 grid grid-cols-2 gap-1">
        {(Object.keys(tribeRep) as TribeId[]).map((tribe) => (
          <div key={tribe} className="flex items-center justify-between gap-2 text-mono-xs">
            <span className="truncate text-[var(--text-secondary)]">{TRIBE_LABEL[tribe]}</span>
            <span
              className={cn('shrink-0 tabular-nums', tribeRep[tribe] >= 0 ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}
              style={tribeRep[tribe] === 0 ? { color: 'var(--text-muted)' } : { color: TRIBE_COLOR[tribe] }}
            >
              {tribeRep[tribe] > 0 ? '+' : ''}{tribeRep[tribe]}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function LifeBlock() {
  const l = useLife();
  return (
    <Section title="Life">
      <Row label="Health" value={`${l.health.toFixed(0)}/100`} bar={l.health} />
      <Row label="Sanity" value={`${l.sanity.toFixed(0)}/100`} bar={l.sanity} />
      <Row label="Marriage" value={`${l.relationships.wife.toFixed(0)}`} barCenter={l.relationships.wife} />
      <Row label="Mom" value={`${l.relationships.mom.toFixed(0)}`} barCenter={l.relationships.mom} />
      <Row label="Best friend" value={`${l.relationships.best_friend.toFixed(0)}`} barCenter={l.relationships.best_friend} />
      <Row label="IRS heat" value={`${l.irsHeat.toFixed(0)}`} bar={l.irsHeat} barColor="var(--negative)" />
      <Row label="SEC heat" value={`${l.secHeat.toFixed(0)}`} bar={l.secHeat} barColor="var(--negative)" />
      <Row label="FBI heat" value={`${l.fbiHeat.toFixed(0)}`} bar={l.fbiHeat} barColor="var(--negative)" />
      <Row label="Credit score" value={l.creditScore.toString()} />
      <Row label="Apartment" value={l.apartmentTier} />
      <Row label="Insurance" value={l.insuranceStatus} />
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <div className="mb-1 text-mono-xs font-bold uppercase tracking-wider text-[var(--accent-primary)]">{title}</div>
      <div className="rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2">
        {children}
      </div>
    </section>
  );
}

function Row({
  label, value, positive, bar, barCenter, barColor,
}: {
  label: string;
  value: string;
  positive?: boolean;
  bar?: number;          // 0..100
  barCenter?: number;    // -100..100, displayed as bidirectional
  barColor?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5 text-mono-xs">
      <span className="text-[var(--text-secondary)]">{label}</span>
      <div className="flex items-center gap-2">
        {bar !== undefined && (
          <div className="h-1 w-20 rounded bg-[var(--bg-elevated)]">
            <div
              className="h-full rounded transition-all"
              style={{ width: `${Math.max(0, Math.min(100, bar))}%`, background: barColor ?? 'var(--accent-primary)' }}
            />
          </div>
        )}
        {barCenter !== undefined && (
          <div className="relative h-1 w-20 rounded bg-[var(--bg-elevated)]">
            <div className="absolute left-1/2 h-1 w-px bg-[var(--text-muted)]" />
            <div
              className="absolute top-0 h-full rounded transition-all"
              style={{
                width: `${Math.abs(barCenter) / 2}%`,
                left: barCenter >= 0 ? '50%' : `${50 - Math.abs(barCenter) / 2}%`,
                background: barCenter >= 0 ? 'var(--positive)' : 'var(--negative)',
              }}
            />
          </div>
        )}
        <span
          className={cn(
            'tabular-nums',
            positive === true && 'text-[var(--positive)]',
            positive === false && 'text-[var(--negative)]',
            positive === undefined && 'text-[var(--text-primary)]',
          )}
        >
          {value}
        </span>
      </div>
    </div>
  );
}
